import { ft3dPartyRelocationGroups, ft3dPartyRelocationScalar, ft3dRoomContainsOccupancy, ft3dSelectionRelocationGroups, ft3dPlanGroupsIntoRoom, ft3dPlanPartyMemberGroupRelocation } from '../port/k2-geometry/ft3d_party_relocation';
import { native, num, record, values } from '../port/k2-geometry/php-shapes';
import { geometryEnvironment } from '../geometry/port-environment';
import { compoundActorIds, creatureZone, liftObstacles, floorBandShape, withExteriorOpenGround, type LiftWorld } from '../inventory/lift-occupancy';
import { circleOccupancy, placementLegality, resolvePlacement, type FtMoveOccupancy } from '../../../../ft3d/engine/ft3d-move-core.js';
import { squeezeBodyFt } from '../../../../ft3d/engine/ft3d-creature-size.js';
import { encounterField, posCoord, layerOf, layerEntriesOf } from '../../shared/array-types';
import { storedStatusRows } from '../compile/creature-status-reader.js';
import { moverMovementInt } from './difficult-terrain.js';
import { physicalBodyNavigationShape } from '../../shared/physical-creature-body';
import { floorBandRoomForPosition } from '../../encounter-creation-engine/floor-band';
import { stairSupportAt, type ShapeBox, type RestShape } from '../inventory/floor-move';
import { retainHeldBase } from '../ability/frame-snapshot';
import { rowScalar } from '../../encounter-map/ft3d/perception';

type Row = Record<string, unknown>;
export interface RelocationSelection { source: Row; ids: string[]; point?: { x: number; y: number; z: number } }
export interface PartyPlacement { id: string; type: 'creature' | 'inventory'; x: number; y: number; z: number; expected_rev: number; source_encounter_id?: string; before: { x_pos: number; y_pos: number; z_pos: number } }
export interface PartyPlan { status: string; reason?: string; roomId: string; anchor?: unknown; placements: PartyPlacement[]; cohorts?: Record<string, string[]> }

/** Bind the K2 formation port to the same held world used by ordinary walks. */
export function planPartyRelocation(campaign: Row, encounter: Row, shape: unknown, roomId: string, world: LiftWorld, selection?: RelocationSelection): PartyPlan {
  const scalar = (raw: unknown, key: string): unknown => ft3dPartyRelocationScalar(raw, key);
  const row = (raw: unknown): Row => native(raw) as Row;
  const id = (raw: unknown): string => String(scalar(raw, 'encounter_creature_id') ?? scalar(raw, 'inventory_id') ?? '').toUpperCase();
  const squeezeRead = { value: layerOf, entries: layerEntriesOf, statuses: (row: Row) => storedStatusRows(row, 'target'),
    definition: (id: string): Row | null => ((encounter.catalog as Row | undefined)?.[id] ?? (selection?.source.catalog as Row | undefined)?.[id]) as Row ?? null };
  const base = (raw: unknown): number => {
    const tier = world.creatures.find(c => c.id.toUpperCase() === id(raw))?.tierFt ?? world.rows.find(r => r.id.toUpperCase() === id(raw))?.tierFt;
    if (!tier || !Number.isFinite(tier)) throw new Error('party-relocation:body-unavailable');
    return tier;
  };
  // Living avatars need size-tier seats; corpses keep their mesh depth as the lying height.
  const seatCreature = (c: LiftWorld['creatures'][number]): LiftWorld['creatures'][number] => !c.dead && c.meshFt && c.tierFt
    ? { ...c, meshFt: [Math.max(c.meshFt[0], c.tierFt), c.meshFt[1], Math.max(c.meshFt[2], c.tierFt)] } : c;
  const occupancy = (raw: unknown, x?: unknown, y?: unknown, z?: unknown): FtMoveOccupancy & { physicalBody: boolean; squeezable: boolean; squeezeSizeFt: number } => {
    const r = row(raw), cid = id(raw), cx = x === undefined ? posCoord(r, 'x_pos') ?? 0 : num(x), cy = y === undefined ? posCoord(r, 'y_pos') ?? 0 : num(y);
    const fact = world.creatures.find(c => c.id.toUpperCase() === cid);
    const original = fact ? creatureZone(seatCreature(fact)) : world.rows.find(r => r.id.toUpperCase() === cid)?.zone;
    if (!original) throw new Error('party-relocation:occupancy-unavailable');
    const feet = z === undefined ? posCoord(r, 'z_pos') ?? 0 : num(z) - base(raw) / 2;
    return { ...original, x: cx, y: cy, zMin: feet, zMax: feet + original.zMax - original.zMin,
      physicalBody: fact?.meshFt != null, squeezable: !!fact && !fact.meshFt,
      squeezeSizeFt: squeezeBodyFt(base(raw), moverMovementInt(r, squeezeRead, 'squeeze_size_category_offset')) } as FtMoveOccupancy & { physicalBody: boolean; squeezable: boolean; squeezeSizeFt: number };
  };
  const reserved: FtMoveOccupancy[] = [];
  const movingIds = new Set<string>();
  const obstaclesByMover = new Map<string, FtMoveOccupancy[]>();
  const point = selection?.point;
  const standingZ = (x: number, y: number, anchorZ: number): number => {
    const floor = floorBandShape(shape as RestShape, x, y, anchorZ)?.floor ?? 0;
    return Math.max(floor, stairSupportAt(shape as RestShape, x, y, anchorZ) ?? floor);
  };
  const pointBand = point ? floorBandShape(shape as never, point.x, point.y, point.z) : null;
  const pointRoom = point && pointBand ? floorBandRoomForPosition(pointBand.shape.boxes, point.x, point.y, point.z) as ShapeBox | null : null;
  const boundedRoom = pointRoom && !pointRoom.navigationOpenGround && pointRoom.creation?.enclosure !== 'none'
    && pointRoom.creation?.environment !== 'exterior-open' && pointRoom.creation?.presetId !== 'exterior-open' ? pointRoom : null;
  const deps: Parameters<typeof ft3dPlanGroupsIntoRoom>[5]['deps'] = {
    encounter_row_creature_id: raw => scalar(raw, 'encounter_creature_id') ?? '',
    encounter_row_is_dead: raw => Number(scalar(raw, 'encounter_creature_dead') ?? 0) !== 0,
    inventory_field: scalar,
    pos_coord: (raw, key) => posCoord(row(raw), key as 'x_pos' | 'y_pos' | 'z_pos') ?? 0,
    ft_compound_actor_ids: (_e, raw) => record(Object.fromEntries([...compoundActorIds(world, id(raw))].map(id => [id, true]))),
    ft_ground_occupancy_base_size: base,
    ft_ground_occupancy_shape: occupancy,
    ft_move_clearance_radius: raw => { const o = row(raw), radius = o.kind === 'circle' ? num(o.r) : Math.hypot(num(o.halfW), num(o.halfD));
      return o.squeezable ? squeezeBodyFt(num(o.squeezeSizeFt), 2) / 2 : radius; },
    ft_move_occupancy_at: (raw, x, y) => ({ ...row(raw), x: num(x), y: num(y) }),
    ft_encounter_occupancy_obstacles: (_e, raw, _moving) => {
      const moverId = id(raw), held = obstaclesByMover.get(moverId);
      if (held) return held;
      const resident = new Set([...values(encounter.encounter_creatures_arr), ...values(encounter.encounter_inventories_arr)].map(id));
      const destination = { ...world, creatures: world.creatures.filter(c => resident.has(c.id.toUpperCase()) && !movingIds.has(c.id.toUpperCase())).map(seatCreature),
        rows: world.rows.filter(r => resident.has(r.id.toUpperCase()) && !movingIds.has(r.id.toUpperCase())) };
      // Pad canonical standing zones once, after thin furnishings and tree canopies are excluded.
      const obstacles = liftObstacles(destination,
        { id: moverId, tierFt: base(raw), team: null }, circleOccupancy(0, 0, 1e6, -1e6, 1e6) as never, true, .01);
      obstaclesByMover.set(moverId, obstacles);
      return obstacles;
    },
    ft_move_resolve_placement: (s, m, obs, _z, _height) => {
      const mover = native(m) as FtMoveOccupancy & { physicalBody?: boolean; squeezable?: boolean; squeezeSizeFt?: number }, height = mover.zMax - mover.zMin;
      // Searching from an enclosed room or derived canvas cannot synthesize free ground outside it.
      const navigation = boundedRoom ? { ...row(s), boxes: [boundedRoom] } : native(s);
      const movementShape = mover.physicalBody ? physicalBodyNavigationShape(navigation as never, height) : navigation;
      const obstacles = [...values(obs).map(o => native(o) as FtMoveOccupancy), ...reserved];
      const exactDrop = !!point && mover.squeezable && Math.hypot(mover.x - point.x, mover.y - point.y) <= 1e-6;
      const result = resolvePlacement(movementShape as never, mover, obstacles,
        { rayZ: (mover.zMin + mover.zMax) / 2, headroomFt: height, allowSqueeze: mover.squeezable, squeezeSizeFt: mover.squeezeSizeFt,
          // GM's explicit standee drop may overlap architecture; endpoint settlement makes it Very Squeezed.
          ignoreClearance: exactDrop });
      if (result.status !== 'placed') return result;
      if (boundedRoom && !exactDrop && !ft3dRoomContainsOccupancy(boundedRoom, { ...mover, x: result.x, y: result.y }, env)) return { status: 'blocked', reason: 'outside_union' };
      if (!point) return result;
      const z = standingZ(result.x, result.y, point.z);
      // A sideways nudge can leave a stair tread. Judge the final seat at its own support height.
      if (Math.abs(z - mover.zMin) > 1e-6) {
        const seat = { ...mover, x: result.x, y: result.y, zMin: z, zMax: z + height };
        const reason = placementLegality(movementShape as never, seat, obstacles,
          { rayZ: z + height / 2, headroomFt: height, allowSqueeze: mover.squeezable, squeezeSizeFt: mover.squeezeSizeFt, ignoreClearance: exactDrop });
        if (reason) return { status: 'blocked', reason };
      }
      return { ...result, z };
    },
    ft_shape_floor_band_for_position: (s, x, y, z) => floorBandShape(native(s) as never, num(x), num(y), num(z))?.floor ?? 0,
    ft_ground_floor_band_shape_for_position: (s,x,y,z) => floorBandShape(native(s) as never,num(x),num(y),num(z)),
    ft_ground_with_exterior_open_ground: (s,x,y,x1,y1,margin,canvas) => withExteriorOpenGround(native(s) as never,num(x),num(y),num(x1),num(y1),num(margin),canvas),
  };
  const env = geometryEnvironment(deps);
  const groups = values(selection ? ft3dSelectionRelocationGroups(selection.source, record(Object.fromEntries(selection.ids.map(id => [id.toUpperCase(), true]))), env)
    : ft3dPartyRelocationGroups(campaign, encounter, env));
  for (const group of groups) for (const member of values((native(group) as Row).rows)) movingIds.add(id(member));
  const blocked = (group: unknown, reason: string): PartyPlan => {
    const member = values((native(group) as Row).rows)[0];
    const name = ['player_name', 'encounter_creature_name', 'item_name'].map(key => String(scalar(member, key) ?? '').trim()).find(Boolean) ?? 'Creature';
    return { status: 'blocked', reason: `no-free-position: ${name} (${reason})`, roomId, placements: [] };
  };
  if (!groups.length) return { status: 'blocked', reason: selection ? 'nothing_to_move' : 'no_active_party', roomId, placements: [] };
  let planned: unknown;
  if (selection?.point) {
    const { point } = selection, placements: PartyPlacement[] = [];
    const floorAt = (x: number, y: number): number => floorBandShape(shape as never, x, y, point.z)?.floor ?? 0;
    const targetFloor = floorAt(point.x, point.y), points: [number, number][] = [[point.x, point.y]];
    for (let ring = 1; ring <= 12; ring++) for (let dy = -ring; dy <= ring; dy++) for (let dx = -ring; dx <= ring; dx++)
      if (Math.max(Math.abs(dx), Math.abs(dy)) === ring) points.push([point.x + dx * 5, point.y + dy * 5]);
    for (const group of groups) {
      let result: PartyPlan | null = null;
      for (const [x, y] of points) {
        if (boundedRoom && !ft3dRoomContainsOccupancy(boundedRoom, { kind: 'circle', x, y, r: 0 }, env)) continue;
        if (Math.abs(floorAt(x, y) - targetFloor) > .01) continue;
        result = native(ft3dPlanPartyMemberGroupRelocation(group, encounter, shape, x, y, standingZ(x, y, point.z), env)) as PartyPlan;
        if (result.status === 'placed') break;
      }
      if (!result || result.status !== 'placed') return blocked(group, result?.reason ?? 'formation-blocked');
      placements.push(...result.placements);
      for (const p of result.placements) {
        const member = values((native(group) as Row).rows).find(r => id(r) === p.id);
        if (member) reserved.push(occupancy(member, p.x, p.y, p.z + base(member) / 2));
      }
    }
    planned = { status: 'placed', roomId, placements };
  } else planned = ft3dPlanGroupsIntoRoom(groups, encounter, shape, roomId, null, env);
  const result = native(planned) as PartyPlan;
  if (selection) result.cohorts = Object.fromEntries(selection.ids.map(seed => [seed.toUpperCase(),
    values(ft3dSelectionRelocationGroups(selection.source, record({ [seed.toUpperCase()]: true }), env))
      .flatMap(group => values((native(group) as Row).rows).map(id))]));
  if (result.status !== 'placed' && 'blockedGroup' in result) return blocked(groups[Number(result.blockedGroup)], result.reason ?? 'formation-blocked');
  const origin = selection?.source ?? encounter;
  const indexed = placementRows(origin);
  result.placements = (result.placements ?? []).map(p => {
    const held = indexed[p.type].get(p.id.toUpperCase())?.row;
    if (!held) throw new Error('party-relocation:row-unavailable');
    return { ...p, source_encounter_id: String(rowScalar(held, p.type === 'creature' ? 'encounter_id' : 'possess_id') ?? ''), expected_rev: Number(rowScalar(held, 'movement_rev') ?? 0), before: { x_pos: posCoord(held, 'x_pos') ?? 0, y_pos: posCoord(held, 'y_pos') ?? 0, z_pos: posCoord(held, 'z_pos') ?? 0 } };
  });
  return result;
}

function placementRows(encounter: Row): Record<PartyPlacement['type'], Map<string, { row: Row; index: number }>> {
  const index = (key: string, idKey: string): Map<string, { row: Row; index: number }> => new Map(
    (Array.isArray(encounter[key]) ? encounter[key] as Row[] : []).map((row, index) =>
      [String(rowScalar(row, idKey) ?? '').toUpperCase(), { row, index }]));
  return { creature: index('encounter_creatures_arr', 'encounter_creature_id'), inventory: index('encounter_inventories_arr', 'inventory_id') };
}

/** Placement borrows the held geometry, definitions and residents; only moved ancestry is writable. */
export function withPlacementDiff(encounter: Row, placements: readonly PartyPlacement[]): Row {
  if (!placements.length) return encounter;
  const indexed = placementRows(encounter), after = retainHeldBase(encounter, { ...encounter });
  const copied = new Set<string>();
  for (const p of placements) {
    const found = indexed[p.type].get(p.id.toUpperCase());
    if (!found) throw new Error('party-relocation:row-unavailable');
    const key = p.type === 'creature' ? 'encounter_creatures_arr' : 'encounter_inventories_arr';
    if (!copied.has(key)) {
      after[key] = [...encounter[key] as Row[]];
      if (p.type === 'inventory' && encounter.inventory === encounter[key]) after.inventory = after[key];
      copied.add(key);
    }
    const rows = after[key] as Row[], member = rows[found.index]!;
    const bucket = p.type === 'creature' ? 'encounter_creatures' : 'inventory';
    const position = { x_pos: p.x, y_pos: p.y, z_pos: p.z };
    const patch = { ...position, ...(p.type === 'creature' ? { movement_rev: p.expected_rev + 1 } : {}) };
    rows[found.index] = retainHeldBase(member, { ...member, position: { ...(member.position as Row | undefined), ...position },
      ...(member[bucket] ? { [bucket]: { ...member[bucket] as Row, ...patch } } : patch) });
  }
  return after;
}

/** Compatible destination boundary facts over a placement projection. */
export function partyRelocationBoundary(encounter: Row, placements: readonly PartyPlacement[]): Row {
  const after = withPlacementDiff(encounter, placements), before_positions: Row = {}, moved_ids: string[] = [];
  for (const p of placements) if (p.type === 'creature') { moved_ids.push(p.id); before_positions[p.id] = p.before; }
  return { encounter_id: String(encounterField(encounter, 'encounter_id') ?? ''), after, before_positions, moved_ids, transfer: false };
}
